import { useEffect, useMemo, useState } from "react";
import { api, payload } from "./lib/api";
import EscapeExperienceWizard, { type EscapeExperience } from "./EscapeExperienceWizard";
import EscapeAgenda from "./EscapeAgenda";
import CustomerBook from "./CustomerBook";

type Slot = { weekday: number; time: string };
type Experience = EscapeExperience & {
  id: string;
  slug: string;
  name: string;
  bookingMode: "schedule" | "inquiry";
  resourceKey: string;
  durationMinutes: number;
  minPartySize: number;
  maxPartySize: number;
  active: boolean;
  slots: Slot[];
};
type Booking = {
  id: string;
  experienceName: string;
  resourceKey: string;
  date: string;
  serviceDate: string;
  time: string;
  partySize: number;
  status: "pending" | "confirmed" | "cancelled";
  source: string;
  customer: { firstName: string; lastName: string; email: string; phone: string };
  notes: string;
  createdAt: string;
};
type Config = {
  enabled: boolean;
  timezone: "Europe/Rome";
  contactPhone: string;
  experiences: Experience[];
  bookings: Booking[];
  subscriberCount: number;
};
type Availability = {
  experiences: Array<Omit<Experience,"slots"> & { slots: Array<{ date: string; time: string; dayOffset: number; lateNight: boolean; available: boolean; reason: string | null }> }>;
};
type EmailSettings = {
  enabled: boolean;
  apiKeyConfigured: boolean;
  ownerEmail: string;
  fromName: string;
  fromAddress: string;
  lastTestAt: string | null;
  lastSentAt: string | null;
  lastError: string | null;
};

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export default function EscapeOperations({ siteId, siteSlug, canEdit }: { siteId: string; siteSlug: string; canEdit: boolean }) {
  const [config, setConfig] = useState<Config | null>(null);
  const [date, setDate] = useState(today);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [tab, setTab] = useState<"esperienze" | "clienti" | "impostazioni">("esperienze");
  const [sub, setSub] = useState<"agenda" | "scheda">("agenda");
  const [busy, setBusy] = useState("");
  const [selectedExperience, setSelectedExperience] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
  const [emailDraft, setEmailDraft] = useState<{ enabled: boolean; ownerEmail: string; fromName: string; fromAddress: string; apiKey: string }>({ enabled: false, ownerEmail: "", fromName: "", fromAddress: "", apiKey: "" });
  const [testEmail, setTestEmail] = useState("");

  async function load() {
    setError("");
    try {
      setConfig(await api<Config>(`/api/sites/${siteId}/escape-operations?date=${date}`));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossibile caricare le prenotazioni");
    }
  }

  async function loadAvailability() {
    try {
      setAvailability(await api<Availability>(`/api/public/escape/${siteSlug}/availability?date=${date}`));
    } catch (cause) {
      setAvailability(null);
      setError(cause instanceof Error ? cause.message : "Impossibile caricare gli orari");
    }
  }

  async function loadEmailSettings() {
    try {
      const result = await api<EmailSettings>(`/api/sites/${siteId}/escape-email`);
      setEmailSettings(result);
      setEmailDraft({ enabled: result.enabled, ownerEmail: result.ownerEmail, fromName: result.fromName, fromAddress: result.fromAddress, apiKey: "" });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Impossibile caricare le notifiche email");
    }
  }

  useEffect(() => { void load(); }, [siteId,date]);
  useEffect(() => { void loadEmailSettings(); }, [siteId]);
  useEffect(() => { if (config?.enabled) void loadAvailability(); }, [config?.enabled, date, siteSlug]);

  async function saveEmailSettings() {
    if (!canEdit) return;
    setBusy("email-save"); setMessage(""); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-email`, payload("PUT", {
        enabled: emailDraft.enabled,
        ownerEmail: emailDraft.ownerEmail,
        fromName: emailDraft.fromName,
        fromAddress: emailDraft.fromAddress,
        apiKey: emailDraft.apiKey,
      }));
      setMessage("Impostazioni email salvate.");
      await loadEmailSettings();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally { setBusy(""); }
  }

  async function sendTestEmail() {
    if (!canEdit || !testEmail) return;
    setBusy("email-test"); setMessage(""); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-email/test`, payload("POST", { email: testEmail }));
      setMessage(`Email di prova inviata a ${testEmail}.`);
      await loadEmailSettings();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Invio di prova non riuscito");
    } finally { setBusy(""); }
  }

  const dayBookings = useMemo(
    () => (config?.bookings || []).filter((booking) => booking.serviceDate === date),
    [config?.bookings, date],
  );

  function updateExperience(id: string, update: Partial<Experience>) {
    setConfig((current) => current ? { ...current, experiences: current.experiences.map((item) => item.id === id ? { ...item, ...update } : item) } : current);
  }

  async function save() {
    if (!config || !canEdit) return;
    const invalid = document.querySelector<HTMLInputElement>('.escape-settings input:invalid');
    if (invalid) {invalid.reportValidity();return;}
    setBusy("save"); setMessage(""); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-operations`, payload("PUT", {
        enabled: config.enabled,
        timezone: config.timezone,
        contactPhone: config.contactPhone,
        experiences: config.experiences,
      }));
      setMessage("Salvato: le modifiche sono già sul sito.");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally { setBusy(""); }
  }

  // L'ordine delle schede qui è l'ordine in home e nel calendario prenotazioni (sort_order = posizione nella lista).
  async function moveExperience(id: string, direction: -1 | 1) {
    if (!config || !canEdit) return;
    const list = [...config.experiences];
    const from = list.findIndex((item) => item.id === id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= list.length) return;
    [list[from], list[to]] = [list[to], list[from]];
    setConfig({ ...config, experiences: list });
    setBusy("save"); setMessage(""); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-operations`, payload("PUT", {
        enabled: config.enabled, timezone: config.timezone, contactPhone: config.contactPhone, experiences: list,
      }));
      setMessage("Ordine salvato: è già cambiato anche sul sito.");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Non sono riuscito a cambiare l'ordine");
      await load();
    } finally { setBusy(""); }
  }

  async function addExperience() {
    if (!canEdit) return;
    const name = window.prompt("Come si chiama la nuova esperienza o pagina? (puoi cambiare tutto dopo)");
    if (!name || name.trim().length < 2) return;
    setBusy("add"); setMessage(""); setError("");
    try {
      const result = await api<{ experience: Experience }>(`/api/sites/${siteId}/escape-operations/experiences`, payload("POST", { name: name.trim() }));
      await load();
      setSelectedExperience(result.experience.id);
      setSub("scheda");
      setMessage("Nuova esperienza creata. Segui i 5 passi: all'ultimo scegli «Online» e salva, e comparirà in home con la sua pagina.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Creazione non riuscita");
    } finally { setBusy(""); }
  }

  async function removeExperience(experience: Experience) {
    if (!canEdit) return;
    if (!window.confirm(`Eliminare definitivamente «${experience.name}»? Se ha già prenotazioni non si potrà eliminare.`)) return;
    setBusy("delete"); setMessage(""); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-operations/experiences/${experience.id}`, { method: "DELETE" });
      setSelectedExperience("");
      await load();
      setMessage("Esperienza eliminata.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Eliminazione non riuscita");
    } finally { setBusy(""); }
  }

  async function setClosed(resourceKey: string, slotDate: string, time: string, closed: boolean) {
    if (!canEdit) return;
    setBusy(`${resourceKey}-${slotDate}-${time}`); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-operations/slot`, payload("PUT", { resourceKey, date: slotDate, time, closed, note: closed ? "Chiuso dal gestore" : "" }));
      setMessage(closed ? "Orario chiuso su tutte le esperienze della stessa location." : "Orario riaperto.");
      await loadAvailability();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Aggiornamento non riuscito");
    } finally { setBusy(""); }
  }

  async function updateBooking(booking: Booking, status: Booking["status"]) {
    if (!canEdit) return;
    setBusy(booking.id); setError("");
    try {
      await api(`/api/sites/${siteId}/escape-bookings/${booking.id}`, payload("PATCH", { status }));
      await Promise.all([load(), loadAvailability()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Aggiornamento non riuscito");
    } finally { setBusy(""); }
  }

  if (!config) return <div className="escape-empty">{error || "Caricamento gestione escape room…"}</div>;

  const current = config.experiences.find((item) => item.id === selectedExperience) || config.experiences[0];
  const statusOf = (item: Experience) => !item.active || item.editorial.visibility === "hidden" ? { label: "Nascosta", tone: "off" }
    : item.editorial.visibility === "development" ? { label: "In sviluppo", tone: "wip" }
    : item.editorial.visibility === "winter" ? { label: "In inverno", tone: "wip" }
    : { label: "Online", tone: "on" };
  const currentSlots = (availability?.experiences.find((item) => item.id === current?.id)?.slots || []).filter((slot) => !slot.lateNight || slot.dayOffset === 1);
  const sharedWith = current ? config.experiences.filter((item) => item.id !== current.id && item.resourceKey === current.resourceKey && item.active).map((item) => item.name) : [];
  const currentBookings = dayBookings.filter((booking) => booking.experienceName === current?.name);

  return <div className="escape-manager">
    <div className="workspace-title escape-head"><div><p className="eyebrow indigo">Adventura</p><h2>Prenotazioni ed esperienze</h2><p>Scegli un'esperienza: ognuna ha la sua agenda e la sua scheda, da modificare un passo alla volta.</p></div><div className="escape-kpis"><span><strong>{config.experiences.length}</strong> esperienze</span><span><strong>{config.subscriberCount}</strong> iscritti</span></div></div>
    <div className="escape-tabs"><button className={tab === "esperienze" ? "active" : ""} onClick={() => setTab("esperienze")}>Le mie esperienze</button><button className={tab === "clienti" ? "active" : ""} onClick={() => setTab("clienti")}>Clienti</button><button className={tab === "impostazioni" ? "active" : ""} onClick={() => setTab("impostazioni")}>Impostazioni e notifiche</button></div>
    {error && <div className="notice">{error}</div>}{message && <div className="notice success">{message}</div>}

    {tab === "esperienze" && current && <>
      <div className="exp-strip" role="tablist" aria-label="Esperienze">
        {config.experiences.map((item) => { const status = statusOf(item); return <button type="button" role="tab" aria-selected={item.id === current.id} key={item.id} className={`exp-card${item.id === current.id ? " is-on" : ""}`} onClick={() => setSelectedExperience(item.id)}>
          <strong>{item.name}</strong>
          <small>{item.editorial.eventKind === "special" ? "Evento a date precise" : "Tutto l'anno"}</small>
          <em className={`exp-status is-${status.tone}`}>{status.label}</em>
        </button>; })}
        <button type="button" className="exp-card exp-add" disabled={!canEdit || busy === "add"} onClick={() => void addExperience()}><strong>＋</strong><small>Nuova esperienza o pagina</small></button>
      </div>

      <div className="exp-head"><h3>{current.name}</h3>
        <div className="exp-move" aria-label="Ordine in home"><button type="button" disabled={!canEdit || busy === "save" || config.experiences[0]?.id === current.id} onClick={() => void moveExperience(current.id, -1)}>← Sposta prima</button><button type="button" disabled={!canEdit || busy === "save" || config.experiences[config.experiences.length - 1]?.id === current.id} onClick={() => void moveExperience(current.id, 1)}>Sposta dopo →</button></div>
        <div className="exp-subtabs"><button className={sub === "agenda" ? "active" : ""} onClick={() => setSub("agenda")}>Prenotazioni</button><button className={sub === "scheda" ? "active" : ""} onClick={() => setSub("scheda")}>Scheda e orari</button></div>
      </div>

      {sub === "agenda" && <EscapeAgenda experience={current} sharedWith={sharedWith} slots={currentSlots} bookings={currentBookings} date={date} onDate={setDate} canEdit={canEdit} busyKey={busy} onClose={setClosed} onBooking={updateBooking} onEditCard={() => setSub("scheda")} />}
      {sub === "scheda" && <EscapeExperienceWizard key={current.id} experience={current} siteId={siteId} disabled={!canEdit || busy === "save"} update={(value) => updateExperience(current.id, value)} onSave={save} saving={busy === "save"} onDelete={() => void removeExperience(current)} />}
    </>}

    {tab === "clienti" && <CustomerBook endpoint={`/api/sites/${siteId}/escape-customers`} canEdit={canEdit} mode="escape" />}

    {tab === "impostazioni" && <div className="escape-settings">
      <section>
        <h3>Prenotazioni sul sito</h3>
        <div className="field-pair"><label>Telefono WhatsApp<input value={config.contactPhone} onChange={(event) => setConfig({ ...config, contactPhone: event.target.value })} /></label><label className="check escape-toggle"><input type="checkbox" checked={config.enabled} onChange={(event) => setConfig({ ...config, enabled: event.target.checked })} /> Prenotazioni online attive</label></div>
        <button className="primary" disabled={!canEdit || busy === "save"} onClick={save}>{busy === "save" ? "Salvataggio…" : "Salva"}</button>
      </section>
      <section><h3>Email quando arriva una richiesta</h3></section>

      <section>
        <p>Quando arriva una nuova richiesta di prenotazione, il gestore riceve subito un'email con il riepilogo (esperienza, data, orario, persone) e i recapiti del cliente.</p>
        <div className="field-pair">
          <label>Email del gestore<input type="email" placeholder="marco@esempio.it" value={emailDraft.ownerEmail} onChange={(event) => setEmailDraft({ ...emailDraft, ownerEmail: event.target.value })} /></label>
          <label className="check escape-toggle"><input type="checkbox" checked={emailDraft.enabled} onChange={(event) => setEmailDraft({ ...emailDraft, enabled: event.target.checked })} /> Notifiche attive</label>
        </div>
        <div className="field-pair">
          <label>Nome mittente<input placeholder="Adventura Escape Room" value={emailDraft.fromName} onChange={(event) => setEmailDraft({ ...emailDraft, fromName: event.target.value })} /></label>
          <label>Indirizzo mittente<input type="email" placeholder="prenotazioni@dominio.it" value={emailDraft.fromAddress} onChange={(event) => setEmailDraft({ ...emailDraft, fromAddress: event.target.value })} /></label>
        </div>
        <div className="field-pair">
          <label>API key Resend {emailSettings?.apiKeyConfigured && <small>(già configurata, lascia vuoto per non cambiarla)</small>}<input placeholder="re_..." value={emailDraft.apiKey} onChange={(event) => setEmailDraft({ ...emailDraft, apiKey: event.target.value })} /></label>
        </div>
        <button className="primary" disabled={!canEdit || busy === "email-save"} onClick={saveEmailSettings}>{busy === "email-save" ? "Salvataggio…" : "Salva impostazioni email"}</button>
        {emailSettings?.lastSentAt && <p className="escape-resource-note">Ultima notifica inviata: {new Date(emailSettings.lastSentAt).toLocaleString("it-IT")}</p>}
        {emailSettings?.lastError && <p className="notice">Ultimo errore: {emailSettings.lastError}</p>}
      </section>
      <section>
        <h3>Invia una email di prova</h3>
        <div className="field-pair">
          <label>Indirizzo di prova<input type="email" placeholder="tua-email@esempio.it" value={testEmail} onChange={(event) => setTestEmail(event.target.value)} /></label>
        </div>
        <button disabled={!canEdit || busy === "email-test" || !testEmail} onClick={sendTestEmail}>{busy === "email-test" ? "Invio…" : "Invia email di prova"}</button>
        {emailSettings?.lastTestAt && <p className="escape-resource-note">Ultimo test: {new Date(emailSettings.lastTestAt).toLocaleString("it-IT")}</p>}
      </section>
        </div>}
  </div>;
}
